/**
 * Sanitize SVG string by removing potentially dangerous elements.
 * Defense-in-depth layer on top of Mermaid's securityLevel: 'strict'.
 */

// Tags allowed inside foreignObject — covers what Mermaid needs for labels
const ALLOWED_FOREIGN_OBJECT_TAGS = new Set([
  'div', 'span', 'p', 'b', 'i', 'em', 'strong', 'br', 'ul', 'ol', 'li',
]);

function sanitizeElement(root: Element): void {
  // Remove script elements
  root.querySelectorAll('script').forEach((el) => el.remove());

  // Remove event handler attributes and javascript: URIs
  root.querySelectorAll('*').forEach((el) => {
    const attrs = Array.from(el.attributes);
    attrs.forEach((attr) => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
      if (['href', 'xlink:href', 'src'].includes(attr.name)) {
        if (attr.value.trim().toLowerCase().startsWith('javascript:')) {
          el.removeAttribute(attr.name);
        }
      }
    });
  });

  // Sanitize foreignObject: allowlist safe tags, remove everything else
  root.querySelectorAll('foreignObject').forEach((fo) => {
    fo.querySelectorAll('*').forEach((child) => {
      if (!ALLOWED_FOREIGN_OBJECT_TAGS.has(child.localName)) {
        child.remove();
      }
    });
  });
}

export function sanitizeSvg(svg: string): string {
  // Fix HTML void elements that break XML parsing (e.g. <br> → <br/>)
  const xmlSafe = svg.replace(/<(br|hr|img|input)(\s[^>]*)?\s*(?<!\/)>/gi, '<$1$2/>');

  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlSafe, 'image/svg+xml');

  // Resolve root element — fall back to HTML parsing if XML fails
  let rootElement: Element;
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    const htmlDoc = parser.parseFromString(svg, 'text/html');
    const svgEl = htmlDoc.querySelector('svg');
    if (!svgEl) return svg;
    rootElement = svgEl;
  } else {
    rootElement = doc.documentElement;
  }

  // Sanitization runs on both code paths
  sanitizeElement(rootElement);

  const serializer = new XMLSerializer();
  return serializer.serializeToString(rootElement);
}
